import React, { useState } from "react"

import { NotificationManager } from "react-notifications"

import { gql, useClient, useMutation } from "hooks/urql"

import Avatar from "components/Messaging/Avatar"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"
import RemoveButton from "components/RemoveButton"

export type User = {
  messagingDisplayName?: string
  id?: string
  type: "Tutor" | "Student" | "Teacher" | "Parent"
  showPath: string
  avatar: {
    thumbUrl: string
  }
}

export type AvatarGroupProps = {
  users: User[]
  messageThreadId: string
  viewerId?: string
  canDelete?: boolean
  includeLinks?: boolean
}

const AvatarGroup: React.FC<AvatarGroupProps> = ({
  users,
  messageThreadId,
  viewerId,
  canDelete = false,
  includeLinks = false,
}) => {
  const client = useClient()
  const [activeUser, setActiveUser] = useState(null)
  const [, removeMutation]: any[] = useMutation(removeStudentMutation, client)

  const removeStudent = () => {
    removeMutation({ messageThreadId, userId: activeUser.id })
      .then(
        result => {
          const { failures } = result.data.messageThreadRemoveUser
          if (failures.length > 0) {
            NotificationManager.error(
              `Failed to remove user: ${failures[0]["message"]}`
            )
          } else {
            NotificationManager.info("User removed")
          }
        },
        () => NotificationManager("Something went wrong")
      )
      .catch(() => NotificationManager("Something went wrong"))
      .finally(() => setActiveUser(null))
  }

  return (
    <>
      <ModalWithProvidedBody
        modalTitle="Remove Student"
        modalClassName="bootstrap-modal"
        hideTrigger
        isOpen={!!activeUser}
      >
        <>
          <div className="modal-body">
            Are you sure? {activeUser?.messagingDisplayName} will no longer have
            any of the messages in this thread.
          </div>

          <div className="modal-footer">
            <div className="w-100 flex justify-between">
              <button
                type="button"
                className="btn solid blue btn-info"
                onClick={() => {
                  setActiveUser(null)
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn solid red btn-danger"
                onClick={removeStudent}
              >
                Yes, remove them
              </button>
            </div>
          </div>
        </>
      </ModalWithProvidedBody>

      <div className="isolate flex space-x-2 overflow-hidden">
        {users.map(user => (
          <span
            className="relative mx-1 max-w-[96px] overflow-hidden overflow-ellipsis pt-1 pr-1 text-center"
            key={user.id}
          >
            {canDelete && user.id !== viewerId && (
              <div className="absolute -top-1 -right-4 z-10">
                <RemoveButton onClick={() => setActiveUser(user)} />
              </div>
            )}
            <div className="flex flex-col items-center space-y-1">
              {includeLinks ? (
                <a
                  href={user.showPath}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Avatar
                    size="medium"
                    src={user.avatar.thumbUrl}
                    alt={user.messagingDisplayName}
                  />
                </a>
              ) : (
                <Avatar
                  size="medium"
                  src={user.avatar.thumbUrl}
                  alt={user.messagingDisplayName}
                />
              )}
              <div>
                {user.id === viewerId ? "You" : user.messagingDisplayName}
              </div>
            </div>
          </span>
        ))}
      </div>
    </>
  )
}

const removeStudentMutation = gql`
  mutation RemoveStudent($messageThreadId: ID!, $userId: ID!) {
    messageThreadRemoveUser(
      messageThreadId: $messageThreadId
      userId: $userId
    ) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`
export default AvatarGroup
